import { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useToast } from '../context/ToastContext';
import { Department, SubDivision, Position, Role, Skill } from '../types';

type OrganizationType =
  | 'department'
  | 'subdivision'
  | 'subDivision'
  | 'position'
  | 'role'
  | 'roles'
  | 'skill'
  | 'salaryGrade'
  | 'salarygrade'
  | 'jobGrade'
  | 'jobgrade'
  | 'policy'
  | 'mapping'
  | 'grade';

interface OrganizationData {
  departments: Department[];
  subDivisions: SubDivision[];
  positions: Position[];
  roles: Role[];
  skills: Skill[];
  salaryGrades: any[];
  jobGrades: any[];
  salaryPolicies: any[];
  positionMappings: any[];
}

interface UseOrganizationDataReturn {
  data: OrganizationData;
  loading: boolean;
  saving: boolean;
  loadData: () => Promise<void>;
  handleDelete: (type: OrganizationType, id: number | string) => Promise<boolean>;
  handleSave: (type: OrganizationType, data: any, onSuccess?: () => void) => Promise<any>;
}

export default function useOrganizationData(): UseOrganizationDataReturn {
  const { showSuccess, showError } = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [subDivisions, setSubDivisions] = useState<SubDivision[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [salaryGrades, setSalaryGrades] = useState<any[]>([]);
  const [jobGrades, setJobGrades] = useState<any[]>([]);
  const [salaryPolicies, setSalaryPolicies] = useState<any[]>([]);
  const [positionMappings, setPositionMappings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const loadData = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await apiClient.get('/hris/organization');
      setDepartments(data.departments || []);
      setSubDivisions(data.subDivisions || []);
      setPositions(data.positions || []);
      setRoles(data.roles || []);
      setSkills(data.skills || []);
      setSalaryGrades(data.salaryGrades || []);
      setJobGrades(data.jobGrades || []);
      setSalaryPolicies(data.salaryPolicies || []);
      setPositionMappings(data.positionMappings || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (type: OrganizationType, id: number | string): Promise<boolean> => {
    try {
      await apiClient.delete(`/hris/organization/${type}/${id}`);
      await loadData();

      // Show success message
      const typeLabels: Record<OrganizationType, string> = {
        department: 'Departemen',
        subdivision: 'Sub Divisi',
        subDivision: 'Sub Divisi',
        position: 'Posisi',
        role: 'Role',
        roles: 'Role',
        skill: 'Skill',
        salaryGrade: 'Golongan Gaji',
        salarygrade: 'Golongan Gaji',
        jobGrade: 'Golongan Jabatan',
        jobgrade: 'Golongan Jabatan',
        policy: 'Kebijakan Gaji',
        mapping: 'Mapping Posisi',
        grade: 'Golongan Gaji',
      };
      const label = typeLabels[type] || 'Data';
      showSuccess(`${label} berhasil dihapus`);

      return true;
    } catch (error: any) {
      console.error('Error deleting:', error);
      const errorMessage = error?.message || 'Terjadi kesalahan saat menghapus data';
      showError(errorMessage);
      return false;
    }
  };

  const handleSave = async (
    type: OrganizationType,
    data: any,
    onSuccess?: () => void
  ): Promise<any> => {
    setSaving(true);
    try {
      let response: any;
      const isEdit = !!data.id;

      if (isEdit) {
        response = await apiClient.put(`/hris/organization/${type}/${data.id}`, data);
      } else {
        response = await apiClient.post(`/hris/organization/${type}`, data);
      }
      await loadData();

      // Show success message
      const typeLabels: Record<OrganizationType, string> = {
        department: 'Departemen',
        subdivision: 'Sub Divisi',
        subDivision: 'Sub Divisi',
        position: 'Posisi',
        role: 'Role',
        roles: 'Role',
        skill: 'Skill',
        salaryGrade: 'Golongan Gaji',
        salarygrade: 'Golongan Gaji',
        jobGrade: 'Golongan Jabatan',
        jobgrade: 'Golongan Jabatan',
        policy: 'Kebijakan Gaji',
        mapping: 'Mapping Posisi',
        grade: 'Golongan Gaji',
      };
      const label = typeLabels[type] || 'Data';
      showSuccess(`${label} berhasil ${isEdit ? 'diperbarui' : 'ditambahkan'}`);

      // If policy_code was changed by backend, return updated data
      if (
        type === 'policy' &&
        response?.policy?.policy_code &&
        response.policy.policy_code !== data.policy_code
      ) {
        onSuccess?.();
        return response; // Return response data so frontend can update form
      }

      onSuccess?.();
      return response || true;
    } catch (error: any) {
      console.error('Error saving:', error);
      const errorMessage = error?.message || 'Terjadi kesalahan saat menyimpan data';
      showError(errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    data: {
      departments,
      subDivisions,
      positions,
      roles,
      skills,
      salaryGrades,
      jobGrades,
      salaryPolicies,
      positionMappings,
    },
    loading,
    saving,
    loadData,
    handleDelete,
    handleSave,
  };
}
